/// <amd-module name="Test/Client/RestClient/ExtendedTcm" />
import { IExtendedTestClient } from "Test/Client/RestClient/Extended";
import { ITestLogWithContinuationToken, TestCaseResultsWithContinuationToken, TestResultsFieldDetailsWithContinuationToken } from "Test/common-util/Contracts/ExtendedTest";
import { FieldDetailsForTestResults, ShallowTestCaseResult, TestLog, TestLogStoreEndpointDetails, TestLogType, PipelineReference, TestOutcome } from "Test/common-util/Contracts/Test";
import { RestClients } from "VSS/Platform/Context";
import { RestClientBase } from "VSS/Platform/RestClientBase";
import { deserializeVssJsonObject } from "VSS/Platform/Util/Serialization";

export interface IExtendedTcmClient extends IExtendedTestClient {
    /**
     * @internal
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} buildId
     * @param {TestLogType} type
     * @param {string} directoryPath
     * @param {string} fileNamePrefix
     * @param {boolean} fetchMetaData
     * @param {number} top
     * @param {String} continuationToken - Header to pass the continuationToken
     * @return Promise<TestLog[]>
     */
    getTestLogsForBuild(
        project: string,
        buildId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
    ): Promise<ITestLogWithContinuationToken>;

    /**
     * @internal
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} runId
     * @param {number} resultId
     * @param {TestLogType} type
     * @param {string} directoryPath
     * @param {string} fileNamePrefix
     * @param {boolean} fetchMetaData
     * @param {number} top
     * @param {String} continuationToken - Header to pass the continuationToken
     * @return Promise<TestLog[]>
     */
    getTestResultLogs(
        project: string,
        runId: number,
        resultId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
    ): Promise<ITestLogWithContinuationToken>;
    
    /**
     * @internal
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} runId
     * @param {number} resultId
     * @param {number} subResultId
     * @param {TestLogType} type
     * @param {string} directoryPath
     * @param {string} fileNamePrefix
     * @param {boolean} fetchMetaData
     * @param {number} top
     * @param {String} continuationToken - Header to pass the continuationToken
     * @return Promise<TestLog[]>
     */
    getTestSubResultLogs(
        project: string,
        runId: number,
        resultId: number,
        subResultId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
    ): Promise<ITestLogWithContinuationToken>;

    /**
     * @internal
     * [Preview API]
     *
     * @param {string} project - Project ID or project name
     * @param {number} runId
     * @param {TestLogType} type
     * @param {string} directoryPath
     * @param {string} fileNamePrefix
     * @param {boolean} fetchMetaData
     * @param {number} top
     * @param {String} continuationToken - Header to pass the continuationToken
     * @return Promise<TestLog[]>
     */
    getTestRunLogs(
        project: string,
        runId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
    ): Promise<ITestLogWithContinuationToken>;

    /**
     * Get a list of results for the pipeline instance.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - Pipeline Id. This is same as build Id.
     * @param stageName - Name of the stage. Maximum supported length for name is 256 character.
     * @param phaseName - Name of the phase. Maximum supported length for name is 256 character.
     * @param jobName - Matrixing in YAML generates copies of a job with different inputs in matrix. JobName is the name of those input. Maximum supported length for name is 256 character.
     * @param outcomes - List of outcome of results
     * @param top - Maximum number of results to return
     * @param continuationToken - Header to pass the continuationToken
     */
    getTestResultsByPipelineWithContinuationToken(project: string, pipelineId: number, stageName?: string, phaseName?: string, jobName?: string, outcomes?: string, top?: number, continuationToken?: String): Promise<TestCaseResultsWithContinuationToken>;
    
}

class ExtendedTcmRestClient extends RestClientBase implements IExtendedTcmClient {

     /**
     * Get a list of results for the pipeline instance.
     * 
     * @param project - Project ID or project name
     * @param pipelineId - Pipeline Id. This is same as build Id.
     * @param stageName - Name of the stage. Maximum supported length for name is 256 character.
     * @param phaseName - Name of the phase. Maximum supported length for name is 256 character.
     * @param jobName - Matrixing in YAML generates copies of a job with different inputs in matrix. JobName is the name of those input. Maximum supported length for name is 256 character.
     * @param outcomes - List of outcome of results
     * @param top - Maximum number of results to return
     * @param continuationToken - Header to pass the continuationToken
     */
    public async getTestResultsByPipelineWithContinuationToken(
        project: string,
        pipelineId: number,
        stageName?: string,
        phaseName?: string,
        jobName?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: String
        ): Promise<TestCaseResultsWithContinuationToken> {

        const queryValues: any = {
            pipelineId: pipelineId,
            stageName: stageName,
            phaseName: phaseName,
            jobName: jobName,
            outcomes: outcomes,
            '$top': top,
        };

        var customHeaderValues: any = {
        };
        if (!!continuationToken) {
            customHeaderValues  = {
                "x-ms-continuationtoken": continuationToken
            };
        }

        return this.beginRequest<Response>({
            apiVersion: "5.1-preview.1",
            routeTemplate: "{project}/_apis/testresults/resultsbypipeline",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            customHeaders : customHeaderValues,
			returnRawResponse: true
        }).then((response: Response) => {
            const nextContinuationToken = response.headers.get(continuationTokenHeaderKey);
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<ShallowTestCaseResult[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestCaseResultsWithContinuationToken;
                });
        });
    }

    public async getTestResultsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext?: string,
        outcomes?:string,
        top?: number,
        continuationToken?: string
    ): Promise<TestCaseResultsWithContinuationToken> {

        const queryValues: any = {
            buildId: buildId,
            publishContext: publishContext,
            outcomes: outcomes,
            "$top": top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/tcm/ResultsByBuild",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "4.1-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<ShallowTestCaseResult[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestCaseResultsWithContinuationToken;
                });
        });
    }

    public async getTestResultsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        releaseEnvid?: number,
        publishContext?: string,
        outcomes?: string,
        top?: number,
        continuationToken?: string
    ): Promise<TestCaseResultsWithContinuationToken> {

        const queryValues: any = {
            releaseId: releaseId,
            releaseEnvid: releaseEnvid,
            publishContext: publishContext,
            outcomes: outcomes,
            "$top": top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/tcm/ResultsByRelease",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "4.1-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<ShallowTestCaseResult[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestCaseResultsWithContinuationToken;
                });
        });
    }

    public getResultGroupsByBuildWithContinuationToken(
        project: string,
        buildId: number,
        publishContext: string,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken> {

        const queryValues: any = {
            buildId: buildId,
            publishContext: publishContext,
            fields: fields && fields.join(","),
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/tcm/ResultGroupsByBuild",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "5.0-preview.2",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        fieldDetails: deserializeVssJsonObject<FieldDetailsForTestResults[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestResultsFieldDetailsWithContinuationToken;
                });
        });
    }


    public getResultGroupsByReleaseWithContinuationToken(
        project: string,
        releaseId: number,
        publishContext: string,
        releaseEnvId?: number,
        fields?: string[],
        continuationToken?: string
    ): Promise<TestResultsFieldDetailsWithContinuationToken> {

        const queryValues: any = {
            releaseId: releaseId,
            publishContext: publishContext,
            releaseEnvId: releaseEnvId,
            fields: fields && fields.join(","),
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/tcm/ResultGroupsByRelease",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "5.0-preview.2",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        fieldDetails: deserializeVssJsonObject<FieldDetailsForTestResults[]>(r),
                        continuationToken: nextContinuationToken
                    } as TestResultsFieldDetailsWithContinuationToken;
                });
        });
    }

    public getTestLogsForBuild(
        project: string,
        buildId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
        ): Promise<ITestLogWithContinuationToken> {

        const queryValues: any = {
            buildId: buildId,
            type: type,
            directoryPath: directoryPath,
            fileNamePrefix: fileNamePrefix,
            fetchMetaData: fetchMetaData,
            top: top,
            continuationToken: continuationToken
        };

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/testresults/testlog",
            routeValues: {
                project: project
            },
            queryParams: queryValues,
            apiVersion: "5.0-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<TestLog[]>(r),
                        continuationToken: nextContinuationToken
                    } as ITestLogWithContinuationToken;
                });
        });
    }

    public getTestResultLogs(
        project: string,
        runId: number,
        resultId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
        ): Promise<ITestLogWithContinuationToken> {

        const queryValues: any = {
            type: type,
            directoryPath: directoryPath,
            fileNamePrefix: fileNamePrefix,
            fetchMetaData: fetchMetaData,
            top: top,
            continuationToken: continuationToken
        };
        
        var customHeaderValues: any = {
        };
        if (!!continuationToken) {
            customHeaderValues  = {
                "x-ms-continuationtoken": continuationToken
            };
        }

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/testresults/runs/{runId}/results/{resultId}/testlog",
            routeValues: {
                project: project,
                runId: runId,
                resultId: resultId
            },
            queryParams: queryValues,
            customHeaders: customHeaderValues,
            apiVersion: "5.0-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<TestLog[]>(r),
                        continuationToken: nextContinuationToken
                    } as ITestLogWithContinuationToken;
                });
        });
    }

    public getTestSubResultLogs(
        project: string,
        runId: number,
        resultId: number,
        subResultId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
        ): Promise<ITestLogWithContinuationToken> {

        const queryValues: any = {
            subResultId: subResultId,
            type: type,
            directoryPath: directoryPath,
            fileNamePrefix: fileNamePrefix,
            fetchMetaData: fetchMetaData,
            top: top
        };
        var customHeaderValues: any = {
        };
        if (!!continuationToken) {
            customHeaderValues  = {
                "x-ms-continuationtoken": continuationToken
            };
        }

        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/testresults/runs/{runId}/results/{resultId}/testlog",
            routeValues: {
                project: project,
                runId: runId,
                resultId: resultId
            },
            queryParams: queryValues,
            customHeaders: customHeaderValues,
            apiVersion: "5.0-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<TestLog[]>(r),
                        continuationToken: nextContinuationToken
                    } as ITestLogWithContinuationToken;
                });
        });
    }

    public getTestRunLogs(
        project: string,
        runId: number,
        type: TestLogType,
        directoryPath?: string,
        fileNamePrefix?: string,
        fetchMetaData?: boolean,
        top?: number,
        continuationToken?: String
        ): Promise<ITestLogWithContinuationToken> {

        const queryValues: any = {
            type: type,
            directoryPath: directoryPath,
            fileNamePrefix: fileNamePrefix,
            fetchMetaData: fetchMetaData,
            top: top
        };
        var customHeaderValues: any = {
        };
        if (!!continuationToken) {
            customHeaderValues  = {
                "x-ms-continuationtoken": continuationToken
            };
        }
        return this.beginRequest<Response>({
            routeTemplate: "{project}/_apis/testresults/runs/{runId}/testlog",
            routeValues: {
                project: project,
                runId: runId
            },
            queryParams: queryValues,
            customHeaders: customHeaderValues,
            apiVersion: "5.0-preview.1",
            returnRawResponse: true
        }).then((response: Response) => {
            const tokenString = response.headers.get(continuationTokenHeaderKey);
            const nextContinuationToken = tokenString;
            return response.text()
                .then((r) => {
                    return {
                        results: deserializeVssJsonObject<TestLog[]>(r),
                        continuationToken: nextContinuationToken
                    } as ITestLogWithContinuationToken;
                });
        });
    }
}

const continuationTokenHeaderKey = "x-ms-continuationtoken";

RestClients.add("IExtendedTcmClient", {
    factory: ExtendedTcmRestClient,
    options: {
        serviceInstanceType: "00000054-0000-8888-8000-000000000000"
    }
});
